import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api'; // Keep for non-upload endpoints
import FaceUploadService from '../../utils/faceUpload'; // NEW IMPORT

// Check face verification status (keep using existing api for non-upload)
export const checkFaceVerificationStatus = createAsyncThunk(
  'faceVerification/checkStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/face-verification/status');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Upload profile face image - USING NEW STANDALONE SERVICE
export const uploadProfileImage = createAsyncThunk(
  'faceVerification/uploadProfile',
  async (file, { rejectWithValue }) => {
    try {
      console.log('📤 Starting upload via FaceUploadService:', file.name);
      
      // Use the standalone service
      const data = await FaceUploadService.uploadProfileImage(file);
      
      console.log('✅ Upload successful via FaceUploadService');
      return data;
    } catch (error) {
      console.error('❌ FaceUploadService error:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// Verify face with selfie - USING NEW STANDALONE SERVICE
export const verifyFace = createAsyncThunk(
  'faceVerification/verify',
  async (selfieFile, { rejectWithValue }) => {
    try {
      console.log('🔍 Verifying face via FaceUploadService');
      
      // Use the standalone service
      const data = await FaceUploadService.verifyFace(selfieFile);
      
      console.log('✅ Verification completed via FaceUploadService');
      return data;
    } catch (error) {
      console.error('❌ Face verification error:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// Reset face verification (keep using existing api)
export const resetFaceVerification = createAsyncThunk(
  'faceVerification/reset',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/face-verification/reset');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// KEEP ALL REDUCERS EXACTLY AS THEY WERE
const initialState = {
  status: 'idle', // 'idle' | 'loading' | 'checking' | 'verifying' | 'uploading'
  isFaceVerified: false,
  hasProfileImage: false,
  profileImageUrl: null,
  lastVerifiedAt: null,
  verificationStatus: null, // 'success' | 'failed' | null
  attemptsRemaining: 3,
  error: null,
  lockedUntil: null
};

const faceVerificationSlice = createSlice({
  name: 'faceVerification',
  initialState,
  reducers: {
    clearVerificationStatus: (state) => {
      state.verificationStatus = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Check status
      .addCase(checkFaceVerificationStatus.pending, (state) => {
        state.status = 'checking';
        state.error = null;
      })
      .addCase(checkFaceVerificationStatus.fulfilled, (state, action) => {
        state.status = 'idle';
        state.isFaceVerified = action.payload.data.isFaceVerified;
        state.hasProfileImage = action.payload.data.hasProfileImage;
        state.lastVerifiedAt = action.payload.data.lastFaceVerifiedAt;
        state.attemptsRemaining = action.payload.data.attemptsRemaining || 3;
        state.lockedUntil = action.payload.data.lockedUntil;
      })
      .addCase(checkFaceVerificationStatus.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
      
      // Upload profile image
      .addCase(uploadProfileImage.pending, (state) => {
        state.status = 'uploading';
        state.error = null;
      })
      .addCase(uploadProfileImage.fulfilled, (state, action) => {
        state.status = 'idle';
        state.hasProfileImage = true;
        state.profileImageUrl = action.payload.data?.faceProfileImage;
        state.isFaceVerified = action.payload.data?.isFaceVerified || false;
      })
      .addCase(uploadProfileImage.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload;
      })
      
      // Verify face
      .addCase(verifyFace.pending, (state) => {
        state.status = 'verifying';
        state.error = null;
        state.verificationStatus = null;
      })
      .addCase(verifyFace.fulfilled, (state, action) => {
        state.status = 'idle';
        state.verificationStatus = action.payload.isVerified ? 'success' : 'failed';
        state.isFaceVerified = action.payload.isVerified;
        state.attemptsRemaining = action.payload.attemptsRemaining || 3;
        
        if (action.payload.isVerified) {
          state.lastVerifiedAt = new Date().toISOString();
        }
        
        if (action.payload.lockedUntil) {
          state.lockedUntil = action.payload.lockedUntil;
        }
      })
      .addCase(verifyFace.rejected, (state, action) => {
        state.status = 'idle';
        state.verificationStatus = 'failed';
        state.error = action.payload;
        state.attemptsRemaining = Math.max(0, state.attemptsRemaining - 1);
      })
      
      // Reset verification
      .addCase(resetFaceVerification.fulfilled, (state) => {
        state.isFaceVerified = false;
        state.verificationStatus = null;
        state.attemptsRemaining = 3;
        state.lockedUntil = null;
      });
  }
});

export const { clearVerificationStatus, clearError, resetState } = faceVerificationSlice.actions;
export default faceVerificationSlice.reducer;